import {
  USER_LOGIN_FAIL,
  USER_LOGIN_REQUEST,
  USER_LOGIN_SUCCESS,
  USER_LOGOUT,
  USER_LIST_FAIL,
  USER_LIST_REQUEST,
  USER_LIST_SUCCESS,
  USER_REGISTER_FAIL,
  USER_REGISTER_REQUEST,
  USER_REGISTER_SUCCESS,
  USER_UPDATE_FAIL,
  USER_UPDATE_REQUEST,
  USER_UPDATE_SUCCESS,
  USER_UPDATE_SUB_FAIL,
  USER_UPDATE_SUB_REQUEST,
  USER_UPDATE_SUB_SUCCESS,
  USER_UPDATE_DEVICE_FAIL,
  USER_UPDATE_DEVICE_REQUEST,
  USER_UPDATE_DEVICE_SUCCESS,
  USER_EXPORT_REQUEST,
  USER_EXPORT_SUCCESS,
  USER_EXPORT_FAIL,
  USER_DELETE_REQUEST,
  USER_DELETE_SUCCESS,
  USER_DELETE_FAIL,
} from "../constants/userConstants";
import axios from "axios";
import createAuthConfig from "../utils/createAuthConfig";

const API_URL = process.env.REACT_APP_API_URL;

export const listUser = () => async (dispatch, getState) => {
  try {
    dispatch({
      type: USER_LIST_REQUEST,
    });

    const config = createAuthConfig();

    const { data } = await axios.get(`${API_URL}/api/users/`, config);

    dispatch({
      type: USER_LIST_SUCCESS,
      payload: data,
    });
  } catch (error) {
    const message =
      error.response && error.response.data.message
        ? error.response.data.message
        : error.message;
    dispatch({
      type: USER_LIST_FAIL,
      payload: message,
    });
  }
};

// ADDED: Action to get current user's details on app load
export const getUserDetails = () => async (dispatch) => {
  try {
    // We can reuse the LOGIN constants to manage the loading state
    dispatch({ type: USER_LOGIN_REQUEST });

    const { data } = await axios.get(
      `${API_URL}/api/users/profile`,
      createAuthConfig(),
    );

    dispatch({ type: USER_LOGIN_SUCCESS, payload: data });
  } catch (error) {
    // This will fire if the cookie is invalid or expired
    dispatch({
      type: USER_LOGIN_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : "Not authorized, token failed",
    });
  }
};

export const login = (email, password) => async (dispatch) => {
  try {
    dispatch({ type: USER_LOGIN_REQUEST });

    const config = {
      headers: {
        "Content-type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
      withCredentials: true,
    };

    const { data } = await axios.post(
      `${API_URL}/api/users/login`,
      { email, password },
      config,
    );

    dispatch({ type: USER_LOGIN_SUCCESS, payload: data });
  } catch (error) {
    dispatch({
      type: USER_LOGIN_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

export const logout = () => async (dispatch) => {
  try {
    // 1. Call the backend logout endpoint to clear the httpOnly cookie.
    await axios.post(`${API_URL}/api/users/logout`, {}, createAuthConfig());

    // 2. Dispatch logout to clear user state from Redux.
    dispatch({ type: USER_LOGOUT });
  } catch (error) {
    // Even if the backend call fails, we can still log the user out on the client.
    // You might want to add more robust error handling here.
    console.error("Logout failed:", error);
    dispatch({ type: USER_LOGOUT });
  }
};

export const register =
  (name, email, password, subscription, demographics = {}, botFields = {}) =>
  async (dispatch) => {
    try {
      dispatch({ type: USER_REGISTER_REQUEST });

      const config = {
        headers: {
          "Content-type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
        withCredentials: true,
      };

      // Wave 1 demographics (arrivalReason, journalHabit) ride along with the
      // account so there is no second request and no extra screen. Both are
      // optional; the server drops anything it does not recognise.
      //
      // botFields carries the honeypot and the signed form token. The server
      // reads them for bot signals and then strips them, so they never reach
      // the user record.
      const { data } = await axios.post(
        `${API_URL}/api/users/register`,
        { name, email, password, subscription, ...demographics, ...botFields },
        config,
      );

      dispatch({ type: USER_REGISTER_SUCCESS, payload: data });
    } catch (error) {
      dispatch({
        type: USER_REGISTER_FAIL,
        payload:
          error.response && error.response.data.message
            ? error.response.data.message
            : error.message,
      });
    }
  };

export const updateProfile = (user) => async (dispatch, getState) => {
  try {
    dispatch({ type: USER_UPDATE_REQUEST });

    const config = createAuthConfig();

    const { data } = await axios.put(
      `${API_URL}/api/users/profile`,
      user,
      config,
    );

    dispatch({ type: USER_UPDATE_SUCCESS, payload: data });
  } catch (error) {
    dispatch({
      type: USER_UPDATE_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

/**
 * Set, update, decline or clear the user's demographic answers.
 *
 *   updateDemographics({ ageBand: "35–44", country: "United Kingdom" })
 *   updateDemographics({ declined: true })   // a skip is an answer; record it
 *   updateDemographics({ clear: true })      // erase from /my-account/my-data
 *
 * Merges the result back into userLogin so the Wave 2 card disappears without
 * needing a reload.
 */
export const updateDemographics = (payload) => async (dispatch, getState) => {
  try {
    dispatch({ type: USER_UPDATE_REQUEST });

    const config = createAuthConfig();

    const { data } = await axios.patch(
      `${API_URL}/api/users/demographics`,
      payload,
      config,
    );

    const {
      userLogin: { userInfo },
    } = getState();

    const merged = { ...userInfo, demographic: data.demographic };

    dispatch({ type: USER_UPDATE_SUCCESS, payload: merged });
    dispatch({ type: USER_LOGIN_SUCCESS, payload: merged });
  } catch (error) {
    dispatch({
      type: USER_UPDATE_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

export const updateProfileSubscription =
  (userId, updatedSubscription) => async (dispatch, getState) => {
    try {
      dispatch({ type: USER_UPDATE_SUB_REQUEST });

      const {
        userLogin: { userInfo },
      } = getState();

      const config = {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${userInfo.token}`,
        },
      };

      const { data } = await axios.put(
        `https://api.nowasteddays.io/api/users/${userId}/subscription`,
        updatedSubscription,
        config,
      );

      dispatch({ type: USER_UPDATE_SUB_SUCCESS, payload: data });
    } catch (error) {
      dispatch({
        type: USER_UPDATE_SUB_FAIL,
        payload:
          error.response && error.response.data.message
            ? error.response.data.message
            : error.message,
      });
    }
  };

export const exportUserData = () => async (dispatch) => {
  try {
    dispatch({ type: USER_EXPORT_REQUEST });

    // Assuming createAuthConfig handles the withCredentials: true needed for the cookie
    const config = createAuthConfig();

    const { data } = await axios.post(
      `${API_URL}/api/users/export`, // Matches the route we set up
      {}, // Empty body for POST request
      config,
    );

    dispatch({
      type: USER_EXPORT_SUCCESS,
      payload: data,
    });

    // Automatically trigger the file download in the browser
    const dataStr =
      "data:text/json;charset=utf-8," +
      encodeURIComponent(JSON.stringify(data.data, null, 2));
    const downloadAnchorNode = document.createElement("a");
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute(
      "download",
      `My_NWD_Data_${new Date().toISOString().split("T")[0]}.json`,
    );
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  } catch (error) {
    dispatch({
      type: USER_EXPORT_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

export const deleteAccount = (password) => async (dispatch) => {
  try {
    dispatch({ type: USER_DELETE_REQUEST });

    const config = createAuthConfig();

    // For an axios DELETE request, the request body needs to be explicitly passed inside the 'data' property of the config object.
    config.data = { password };

    const { data } = await axios.delete(
      `${API_URL}/api/users/delete`, // Matches the route we set up
      config,
    );

    dispatch({
      type: USER_DELETE_SUCCESS,
      payload: data,
    });

    // 1. Dispatch logout to clear the user from Redux state immediately
    dispatch({ type: USER_LOGOUT });

    // Note: The backend controller we built already clears the HTTP-only cookie during this request.
  } catch (error) {
    dispatch({
      type: USER_DELETE_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};
